/**
 * Hook otimizado para carregamento de configurações com cache inteligente
 * 
 * Implementa as soluções de design para eliminar loops infinitos de requisições
 * settings conforme especificado na documentação.
 */

import { useState, useCallback, useRef, useEffect, useContext } from 'react';
import { AuthContext } from '../context/Auth/AuthContext';
import useSettings from './useSettings';
import settingsCache from '../utils/settingsCache';

/**
 * Hook que fornece configurações com cache inteligente para evitar loops infinitos
 * 
 * Características:
 * - Cache automático por empresa com TTL
 * - Debounce para múltiplas requisições simultâneas
 * - Fallback para localStorage em caso de erro
 * - Cleanup automático de memory leaks
 * - Controle preciso de estado de carregamento
 * 
 * @returns {Object} Estado e funções para gerenciar settings
 */
const useCachedSettings = () => {
  const { user } = useContext(AuthContext);
  const { getAll: getOriginalSettings } = useSettings();
  
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);
  
  // Refs para cleanup e controle de estado
  const isMountedRef = useRef(true);
  const listenerCleanupRef = useRef(null);
  const debounceTimerRef = useRef(null);
  
  const companyId = user?.companyId;

  /**
   * Registra listener para mudanças no cache
   */
  useEffect(() => {
    if (!companyId) return;

    // Limpar listener anterior se existir
    if (listenerCleanupRef.current) {
      listenerCleanupRef.current();
    }

    // Registrar novo listener
    listenerCleanupRef.current = settingsCache.addListener((updatedCompanyId, updatedSettings) => {
      if (updatedCompanyId === companyId && isMountedRef.current) {
        setSettings(updatedSettings);
        setLoaded(true);
        console.log(`🔄 Settings atualizados via cache listener para empresa ${companyId}`);
      }
    });

    return () => {
      if (listenerCleanupRef.current) {
        listenerCleanupRef.current();
        listenerCleanupRef.current = null;
      }
    };
  }, [companyId]);

  /**
   * Cleanup ao desmontar componente
   */
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      
      // Limpar listener
      if (listenerCleanupRef.current) {
        listenerCleanupRef.current();
      }
      
      // Limpar timer de debounce
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  /**
   * Carrega settings do cache ou servidor com controle de debounce
   */
  const loadSettings = useCallback(async (forceRefresh = false) => {
    if (!companyId) {
      console.warn('⚠️ Tentativa de carregar settings sem companyId');
      return {};
    }

    // Verificar se já está carregando para evitar múltiplas requisições
    if (settingsCache.isLoading(companyId) && !forceRefresh) {
      console.log(`⏳ Settings já estão sendo carregados para empresa ${companyId}`);
      return settings;
    }

    // Verificar cache primeiro (se não for refresh forçado)
    if (!forceRefresh) {
      const cachedSettings = settingsCache.get(companyId);
      if (cachedSettings) {
        
        if (isMountedRef.current) {
          setSettings(cachedSettings);
          setLoaded(true);
          setError(null);
        }
        return cachedSettings;
      }
    }

    try {
      if (isMountedRef.current) {
        setLoading(true);
        setError(null);
      }
      
      // Marcar como carregando
      settingsCache.setLoading(companyId, true);
      
      console.log(`🔄 Carregando configurações do servidor para empresa ${companyId}...`);
      
      const params = { companyId };
      const settingsData = await getOriginalSettings(params);
   
      // Converter array para objeto para facilitar acesso
      const settingsMap = {};
      if (Array.isArray(settingsData)) {
        settingsData.forEach(setting => {
          settingsMap[setting.key] = setting.value;
        });
      }

      // Atualizar cache
      settingsCache.set(companyId, settingsMap);

      // Atualizar estado local se componente ainda está montado
      if (isMountedRef.current) {
        setSettings(settingsMap);
        setLoaded(true);
      }

      console.log(`✅ Configurações carregadas para empresa ${companyId}:`, {
        settingsCount: Object.keys(settingsMap).length,
        fromCache: false
      });

      return settingsMap;

    } catch (err) {
      console.error(`❌ Erro ao carregar configurações para empresa ${companyId}:`, err);
      
      if (isMountedRef.current) {
        setError(err.message || 'Erro ao carregar configurações');
      }

      // 🔄 Tentar carregar do localStorage como fallback
      if (!forceRefresh) {
        console.log(`🔄 Tentando fallback para localStorage da empresa ${companyId}...`);
        
        const fallbackSettings = settingsCache.loadFromLocalStorage(companyId);
        if (fallbackSettings && isMountedRef.current) {
          setSettings(fallbackSettings);
          setLoaded(true);
          setError(null); // Limpar erro se fallback funcionou
          console.log(`💿 Fallback bem-sucedido para empresa ${companyId}`);
          return fallbackSettings;
        }
        
        // 🎯 Fallback para settings padrão se tudo falhar
        const defaultSettings = {
          showTabLabels: "false",
          showTabBadges: "true", 
          showActionButtons: "true",
          enableFiltersSection: "true",
          sendSignMessage: "enabled"
        };
        
        if (isMountedRef.current) {
          setSettings(defaultSettings);
          setLoaded(true);
          console.log(`🎯 Settings padrão aplicados para empresa ${companyId}`);
        }
        
        return defaultSettings;
      }

      throw err;
    } finally {
      // Marcar como não carregando
      settingsCache.setLoading(companyId, false);
      
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [companyId, getOriginalSettings, settings]);

  /**
   * Carrega settings com retry automático em caso de falha
   */
  const loadSettingsWithRetry = useCallback(async (forceRefresh = false, maxRetries = 2) => {
    let lastError = null;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`🔄 Tentativa ${attempt}/${maxRetries} de carregar settings para empresa ${companyId}`);
        return await loadSettings(forceRefresh);
      } catch (error) {
        lastError = error;
        console.warn(`⚠️ Tentativa ${attempt} falhou:`, error.message);
        
        // Se não é a última tentativa, aguardar antes de tentar novamente
        if (attempt < maxRetries) {
          const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000); // Backoff exponencial, máx 5s
          console.log(`⏱️ Aguardando ${delay}ms antes da próxima tentativa...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    // Se chegou aqui, todas as tentativas falharam
    console.error(`❌ Todas as ${maxRetries} tentativas falharam para empresa ${companyId}`);
    throw lastError;
  }, [companyId, loadSettings]);

  /**
   * Carrega settings com debounce para evitar múltiplas chamadas
   */
  const loadSettingsWithDebounce = useCallback((forceRefresh = false, delay = 100) => {
    // Limpar timer anterior
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Configurar novo timer
    debounceTimerRef.current = setTimeout(() => {
      loadSettingsWithRetry(forceRefresh, 2); // Usar retry por padrão
    }, delay);
  }, [loadSettingsWithRetry]);

  /**
   * Invalida cache e recarrega settings
   */
  const invalidateAndReload = useCallback(() => {
    if (!companyId) return;
    
    console.log(`🗑️ Invalidando cache e recarregando settings para empresa ${companyId}`);
    
    settingsCache.invalidate(companyId);
    
    if (isMountedRef.current) {
      setLoaded(false);
      setSettings({});
    }
    
    return loadSettingsWithRetry(true, 3); // Máximo 3 tentativas para reload
  }, [companyId, loadSettingsWithRetry]);

  /**
   * Obtém valor específico de configuração com valor padrão
   */
  const getSetting = useCallback((key, defaultValue = null) => {
    const value = settings[key];
    return value !== undefined ? value : defaultValue;
  }, [settings]);

  /**
   * Atualiza configuração específica no cache e estado local
   */
  const updateSetting = useCallback((key, value) => {
    if (!companyId || !isMountedRef.current) return;

    const updatedSettings = { ...settings, [key]: value };
    
    // Atualizar estado local
    setSettings(updatedSettings);
    
    // Atualizar cache
    settingsCache.set(companyId, updatedSettings);

    console.log(`📝 Setting atualizado: ${key} = ${value} para empresa ${companyId}`);
  }, [companyId, settings]);

  /**
   * Carrega settings automaticamente quando companyId está disponível
   */
  useEffect(() => {
    if (companyId && !loaded && !loading) {
      // Verificar cache primeiro
      const cachedSettings = settingsCache.get(companyId);
      if (cachedSettings) {
        setSettings(cachedSettings);
        setLoaded(true);
        console.log(`🎯 Settings carregados do cache na inicialização para empresa ${companyId}`);
      } else {
        // Carregar do servidor se não há cache
        loadSettingsWithDebounce(false, 50);
      }
    }
  }, [companyId, loaded, loading, loadSettingsWithDebounce]);

  /**
   * Reset quando companyId muda (troca de empresa/usuário)
   */
  useEffect(() => {
    if (!companyId) {
      setSettings({});
      setLoaded(false);
      setLoading(false);
      setError(null);
    }
  }, [companyId]);

  return {
    // Estado
    settings,
    loading,
    loaded,
    error,
    
    // Funções principais
    loadSettings,
    loadSettingsWithRetry,
    invalidateAndReload,
    getSetting,
    updateSetting,
    
    // Flags de controle
    isLoading: loading || settingsCache.isLoading(companyId),
    hasSettings: Object.keys(settings).length > 0,
    companyId
  };
};

export default useCachedSettings;